import React, { useContext, useEffect, useState } from "react";
import { apiGet, apiDelete } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { useTranslation } from "react-i18next";
import AuthContext from "context/AuthContext";
import Pagination from "../Pagination";
import { isEmpty } from "lodash";
import DynamicTable from "../../components/DynamicTable";
import AddPriceModal from "./AddPriceModal";
import EditPriceModal from "./EditPriceModal";
import useToastContext from "hooks/useToastContext";
import helper from "../../utils/helpers";
import Select from "react-select";

const TAB_LIST = "list";

function Price() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });
  const [prices, setPrices] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [isDelete, setIsDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({
    sort_key: "date",
    sort_type: "desc",
  });
  const [defaultTab, setDefaultTab] = useState(TAB_LIST);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const notification = useToastContext();
  const [products, setProducts] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filters, setFilters] = useState({
    product_id: "",
    districtId: "",
    areaId: "",
  });

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPage(newPage);
  };

  const fetchFilterOptions = async () => {
    try {
      const [productsRes, districtsRes] = await Promise.all([
        apiGet(`${apiPath.ListProduct}?all=true`),
        apiGet(`${apiPath.districtList}?limit=1000`),
      ]);

      setProducts(productsRes?.data?.results || []);
      setDistricts(districtsRes?.data?.results?.docs || []);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const fetchAreas = async (districtId) => {
    if (!districtId) {
      setAreas([]);
      return;
    }
    try {
      const res = await apiGet(`${apiPath.areaList}?district_id=${districtId}`);
      setAreas(res?.data?.results || []);
    } catch (error) {
      console.error("Error fetching areas:", error);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchAreas(filters.districtId);
  }, [filters.districtId]);

  const getAllPrices = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const queryParams = new URLSearchParams({
        page: page,
        limit: pageSize,
        sort_key: sort.sort_key,
        sort_type: sort.sort_type,
        ...(filters.product_id && { product_id: filters.product_id }),
        ...(filters.districtId && { district_id: filters.districtId }),
        ...(filters.areaId && { area_id: filters.areaId }),
      });

      const response = await apiGet(
        `${apiPath.ListPrice}?${queryParams.toString()}`
      );

      const result = response?.data;

      if (result?.success) {
        setPrices(result.results?.docs || []);
        setPaginationObj({
          ...paginationObj,
          page: result.results.page,
          pageCount: result.results.totalPages,
          perPageItem: result.results.docs.length,
          totalItems: result.results.totalDocs,
        });
      } else {
        setError("Failed to load prices. Please try again.");
      }
    } catch (error) {
      setError("Failed to load prices. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (price) => {
    try {
      const result = await apiDelete(`${apiPath.deletePrice}/${price._id}`);
      if (result?.data?.success) {
        notification.success(result.data.message);
        getAllPrices();
      } else {
        notification.error(result.data.message || "Failed to delete price");
      }
    } catch (error) {
      notification.error("Failed to delete price");
    }
  };

  useEffect(() => {
    getAllPrices();
    // eslint-disable-next-line
  }, [page, sort, isAddModalOpen, isEditModalOpen, filters]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "districtId" && { areaId: "" }),
    }));
    setPage(1);
  };

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  const columns = [
    {
      key: "date",
      label: "Date",
      render: (value) => (value ? new Date(value).toLocaleDateString() : ""),
    },
    {
      key: "productDetails.product_name",
      label: "Product Name",
      render: (value, row) => row.productDetails?.product_name || "",
    },
    { key: "retail_spot_price", label: "Retail Spot Price" },
    {
      key: "retail_unit",
      label: "Retail Unit",
      render: (value, row) => (row.retail_unit ? row.retail_unit : ""),
    },
    {
      key: "bn_retail_unit",
      label: "Retail Unit(BN)",
      render: (value, row) => (row.bn_retail_unit ? row.bn_retail_unit : ""),
    },
    {
      key: "wholesale_unit",
      label: "Wholesale Unit",
      render: (value, row) => (row.wholesale_unit ? row.wholesale_unit : ""),
    },

    { key: "whoelsale_spot_price", label: "Wholesale Price" },
    { key: "type", label: "Type" },
    {
      key: "districtData.name",
      label: "District",
      render: (value, row) => row.districtData?.name || "",
    },
    {
      key: "areaData.name",
      label: "Area (Bazar)",
      render: (value, row) => row.areaData?.name || "",
    },
  ];

  const actions = {
    view: true,
    edit: true,
    delete: true,
    onEdit: (price) => {
      setSelectedPrice(price);
      setIsAddModalOpen(true);
    },
    onDelete: (price) => {
      helper.alertFunction(
        `${t("ARE_YOU_SURE_TO_DELETE")} this price?`,
        price,
        handleDelete,
        true
      );
    },
  };

  return (
    <div className="bg-[#F9F9F9]">
      <div className="bg-white rounded-lg">
        <div className="pt-4">
          {/* <div className="flex border-b justify-between">
            <div className="flex justify-end w-full">
              <button
                title={t("ADD_PRICE")}
                type="button"
                className="bg-LightBlue flex text-sm px-8 ml-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap"
                onClick={() => setIsAddModalOpen(true)}
              >
                + {t("ADD_PRICE")}
              </button>
            </div>
          </div> */}
          {defaultTab === TAB_LIST ? (
            <div className="p-3">
              <div className="mb-4 flex items-end gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product
                  </label>
                  <Select
                    options={products.map((p) => ({
                      value: p._id,
                      label:
                        p.product_name +
                        (p.sub_title ? ` - ${p.sub_title}` : ""),
                    }))}
                    placeholder="Select Product"
                    isClearable
                    value={
                      products
                        .map((p) => ({
                          value: p._id,
                          label:
                            p.product_name +
                            (p.sub_title ? ` - ${p.sub_title}` : ""),
                        }))
                        .find(
                          (option) => option.value === filters.product_id
                        ) || null
                    }
                    onChange={(option) =>
                      handleFilterChange(
                        "product_id",
                        option ? option.value : ""
                      )
                    }
                    classNamePrefix="react-select"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    District
                  </label>
                  <Select
                    options={districts.map((d) => ({
                      value: d.districtId,
                      label: d.name,
                    }))}
                    placeholder="Select District"
                    isClearable
                    value={
                      districts
                        .map((d) => ({
                          value: d.districtId,
                          label: d.name,
                        }))
                        .find(
                          (option) => option.value === filters.districtId
                        ) || null
                    }
                    onChange={(option) =>
                      handleFilterChange(
                        "districtId",
                        option ? option.value : ""
                      )
                    }
                    classNamePrefix="react-select"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Area
                  </label>
                  <Select
                    options={areas.map((a) => ({
                      value: a.areaId,
                      label: a.name,
                    }))}
                    placeholder="Select Area"
                    isClearable
                    value={
                      areas
                        .map((a) => ({
                          value: a.areaId,
                          label: a.name,
                        }))
                        .find((option) => option.value === filters.areaId) ||
                      null
                    }
                    onChange={(option) =>
                      handleFilterChange("areaId", option ? option.value : "")
                    }
                    isDisabled={!filters.districtId}
                    classNamePrefix="react-select"
                  />
                </div>
                <div>
                  <button
                    title={t("ADD_PRICE")}
                    type="button"
                    className="bg-LightBlue flex text-sm px-8 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap h-[38px]"
                    onClick={() => setIsAddModalOpen(true)}
                  >
                    + {t("ADD_PRICE")}
                  </button>
                </div>
              </div>
              {isLoading ? (
                <div className="text-center py-4">Loading...</div>
              ) : (
                <DynamicTable
                  data={prices}
                  columns={columns}
                  sort={sort}
                  setSort={setSort}
                  actions={actions}
                  page={page}
                  manager={{}}
                  userRole={user?.role}
                  onStatusChange={() => {}}
                  onDelete={() => {}}
                />
              )}
              <div className="mt-4">
                {paginationObj?.totalItems ? (
                  <Pagination
                    handlePageClick={handlePageClick}
                    options={paginationObj}
                    isDelete={isDelete}
                    page={page}
                  />
                ) : null}
              </div>
            </div>
          ) : (
            <div className="p-3">{/* Add price form will go here */}</div>
          )}
        </div>
      </div>
      <AddPriceModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedPrice(null); // Reset selected price on close
        }}
        onSuccess={() => {
          getAllPrices();
          // setIsAddModalOpen(false);
          setSelectedPrice(null);
        }}
        editData={selectedPrice} // Pass selectedPrice for editing
      />
      <EditPriceModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        price={selectedPrice}
        onSuccess={getAllPrices}
      />
    </div>
  );
}

export default Price;
